/**
 * Convert a CSS length value (px / rem / em string OR a bare number) into
 * a pixel number for use in JS layout math.
 *
 * Why this exists: several legacy components do `parseFloat(prop)` on a
 * "Npx" string and treat the result as pixels. That breaks the moment a
 * caller passes a rem string ("1.5rem" → 1.5, not 24), so we centralize
 * the conversion here.
 *
 * Supported inputs:
 *   - number               → returned as-is
 *   - "<n>px" / "<n>"      → parseFloat
 *   - "<n>rem"             → n * rootFontSizePx (live-read from <html>)
 *   - "<n>em" (heuristic)  → n * rootFontSizePx (caller-context em is rare here)
 *   - anything else        → NaN (caller decides fallback)
 */

const ROOT_FONT_SIZE_FALLBACK_PX = 16;

function getRootFontSizePx(): number {
  if (typeof document === "undefined") return ROOT_FONT_SIZE_FALLBACK_PX;
  const computed = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(computed) && computed > 0 ? computed : ROOT_FONT_SIZE_FALLBACK_PX;
}

export function cssLengthToPx(value: string | number | undefined | null): number {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;
  const trimmed = value.trim();
  if (!trimmed) return NaN;

  const match = /^(-?\d*\.?\d+)\s*([a-zA-Z%]*)$/.exec(trimmed);
  // match[1] is guaranteed present when the regex matches (capture group 1 is required).
  if (!match || match[1] === undefined) return NaN;
  const n = parseFloat(match[1]);
  if (Number.isNaN(n)) return NaN;
  const unit = (match[2] || "px").toLowerCase();

  switch (unit) {
    case "px":
    case "":
      return n;
    case "rem":
    case "em":
      return n * getRootFontSizePx();
    default:
      return NaN;
  }
}
